import { useEffect, useMemo, useRef, useState } from 'react';
import {
  KNOWN_UPSTREAM_PROTOCOLS,
  type UpstreamModelCapabilityRecord,
  type UpstreamModelDiscoveryConfig,
  type UpstreamProtocol,
} from '@nadoutong/contracts';
import {
  useUpstreamModelCosts,
  useSetUpstreamModelCosts,
} from '../../api/upstreamModelCosts.ts';
import {
  useBatchIgnoreUpstreamCapabilities,
  useBatchProbeUpstreamCapabilities,
  useBatchSetUpstreamCapabilityProbeProtocol,
  useBatchUnignoreUpstreamCapabilities,
  useDiscoverUpstreamCapabilities,
  useIgnoreUpstreamCapability,
  useProbeUpstreamCapability,
  usePreviewUpstreamCapabilityMutation,
  useSetUpstreamCapabilityProbeProtocol,
  useUnignoreUpstreamCapability,
  useUpstreamCapabilities,
  useUpstreamChannels,
  useUpstreamSkuRoutingSummary,
} from '../../api/useUpstreams.ts';
import { capabilityStatusLabel } from '../../pages/capabilityStatus.ts';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  Select,
  Skeleton,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import type { ChipTone, SelectOption } from '../../ui/index.ts';
import { formatLocalTimestamp } from '../formatters.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import {
  CapabilityImpactDialog,
  type PendingCapabilityMutation,
} from './CapabilityImpactDialog.tsx';
import { ManualDiscoverDialog } from './ManualDiscoverDialog.tsx';
import { ModelCostCell } from './ModelCostCell.tsx';
import type { ModelCostIdentity, ModelCostSaveInput } from './ModelCostCell.tsx';
import { ModelCostWindowsEditor } from './ModelCostWindowsEditor.tsx';
import {
  countServedSkusByCapability,
  groupCapabilitiesByNamespace,
  indexModelCosts,
  modelCostFor,
} from './modelSkuGrouping.ts';
import type { ModelSkuGroup } from './modelSkuGrouping.ts';
import { ServingSkuBadge } from './ServingSkuBadge.tsx';

/**
 * Upstream model SKU table — the supply half of the domain re-partition
 * (epic #1327 PR-5), grouped by upstream namespace.
 *
 * This surface owns exactly what the upstream is the authority for: which
 * models it exposes, how each is classified and probed, whether it is
 * enabled, and what it costs to buy at (channel × physical model) grain.
 *
 * It deliberately owns **no** marketplace write. Target binding, per-SKU
 * economics, route roles, and pool membership moved to the SKU detail
 * page in PR-4, which is now the single write hub for "which supply
 * serves this product". Re-adding any of them here would restore two
 * writers for one fact — the exact drift this epic removed — so the
 * served-SKU column is a count plus a read-only link, nothing more.
 */

const CAPABILITY_STATUS_TONE: Record<string, ChipTone> = {
  passed: 'ok',
  needs_probe: 'neutral',
  probe_failed: 'bad',
  missing_from_list: 'neutral',
  mismatch: 'bad',
  ignored: 'neutral',
};

/**
 * Anchor for in-page deep links. Section 1's probe-readiness fact points here
 * because protocol classification — the thing that clears it — is a control in
 * this table, not in any drawer.
 */
export const UPSTREAM_MODEL_SKU_SECTION_ID = 'upstream-model-skus';

const PROBE_PROTOCOLS: readonly UpstreamProtocol[] = KNOWN_UPSTREAM_PROTOCOLS;
const UNCLASSIFIED = '__unclassified__';
const PROBE_POLL_INTERVAL_MS = 2500;
const PROBE_POLL_TIMEOUT_MS = 120_000;

type BulkAction = 'probe' | 'enable' | 'disable' | `protocol_${UpstreamProtocol}`;

function isProbeable(capability: UpstreamModelCapabilityRecord): boolean {
  return capability.status !== 'missing_from_list' &&
    capability.status !== 'ignored' &&
    capability.probe_protocol !== null;
}

interface UpstreamModelSkuTableProps {
  readonly upstreamId: string;
  /**
   * The upstream's discovery configuration, read on the detail page.
   * `manual` swaps the per-namespace pull button for a paste dialog;
   * `compatible` / `list` keep the one-click discover unchanged.
   */
  readonly modelDiscovery?: UpstreamModelDiscoveryConfig;
}

export function UpstreamModelSkuTable({ upstreamId, modelDiscovery }: UpstreamModelSkuTableProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const capabilitiesQ = useUpstreamCapabilities(upstreamId);
  const channelsQ = useUpstreamChannels(upstreamId);
  const costsQ = useUpstreamModelCosts(upstreamId);
  const routingSummaryQ = useUpstreamSkuRoutingSummary(upstreamId);

  const discover = useDiscoverUpstreamCapabilities(upstreamId);
  const probeCapability = useProbeUpstreamCapability(upstreamId);
  const setProbeProtocol = useSetUpstreamCapabilityProbeProtocol(upstreamId);
  const ignoreCapability = useIgnoreUpstreamCapability(upstreamId);
  const unignoreCapability = useUnignoreUpstreamCapability(upstreamId);
  const batchProbe = useBatchProbeUpstreamCapabilities(upstreamId);
  const batchSetProbeProtocol = useBatchSetUpstreamCapabilityProbeProtocol(upstreamId);
  const batchIgnore = useBatchIgnoreUpstreamCapabilities(upstreamId);
  const batchUnignore = useBatchUnignoreUpstreamCapabilities(upstreamId);
  const previewMutation = usePreviewUpstreamCapabilityMutation(upstreamId);
  const setModelCosts = useSetUpstreamModelCosts(upstreamId);

  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [pendingMutation, setPendingMutation] = useState<PendingCapabilityMutation | null>(null);
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const [discoveringChannelId, setDiscoveringChannelId] = useState<string | null>(null);
  const [manualDiscoverChannel, setManualDiscoverChannel] = useState<
    { readonly channelId: string; readonly channelSlug: string } | null
  >(null);
  const [savingCapabilityId, setSavingCapabilityId] = useState<string | null>(null);
  const [probingCapabilityId, setProbingCapabilityId] = useState<string | null>(null);
  const [pollingIds, setPollingIds] = useState<readonly string[]>([]);
  const pollingStartedAtRef = useRef<Map<string, number>>(new Map());

  const capabilities = useMemo(() => capabilitiesQ.data ?? [], [capabilitiesQ.data]);
  const channels = useMemo(() => channelsQ.data ?? [], [channelsQ.data]);
  const groups = useMemo(
    () => groupCapabilitiesByNamespace(capabilities, channels),
    [capabilities, channels],
  );
  const costIndex = useMemo(() => indexModelCosts(costsQ.data ?? []), [costsQ.data]);
  /**
   * A bounded summary cannot answer "how many SKUs does this model
   * serve" for any row — the missing page could hold the very SKU an
   * operator is looking for — so the count becomes unknown table-wide
   * rather than a per-row guess.
   */
  const summaryTruncated = routingSummaryQ.isSuccess &&
    routingSummaryQ.data.pagination.has_more;
  const servedCounts = useMemo(
    () => countServedSkusByCapability(routingSummaryQ.data?.identities ?? [], capabilities),
    [capabilities, routingSummaryQ.data],
  );
  const servedCountFor = (capabilityId: string): number | null =>
    !routingSummaryQ.isSuccess || summaryTruncated ? null : servedCounts.get(capabilityId) ?? 0;

  const visibleIds = useMemo(
    () => new Set(groups.flatMap((group) => group.rows.map((row) => row.capability.id))),
    [groups],
  );
  useEffect(() => {
    setSelectedIds((previous) => {
      const next = new Set([...previous].filter((id) => visibleIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleIds]);

  const pollingIdSet = useMemo(() => new Set(pollingIds), [pollingIds]);
  useEffect(() => {
    if (pollingIds.length === 0) return;
    let cancelled = false;

    async function refreshAndReconcile(): Promise<void> {
      const result = await capabilitiesQ.refetch();
      if (cancelled) return;
      const rowsById = new Map((result.data ?? []).map((row) => [row.id, row]));
      const now = Date.now();
      setPollingIds((previous) => previous.filter((id) => {
        const row = rowsById.get(id);
        if (row !== undefined && row.status !== 'needs_probe') {
          pollingStartedAtRef.current.delete(id);
          return false;
        }
        const startedAt = pollingStartedAtRef.current.get(id) ?? now;
        if (now - startedAt >= PROBE_POLL_TIMEOUT_MS) {
          pollingStartedAtRef.current.delete(id);
          return false;
        }
        return true;
      }));
    }

    void refreshAndReconcile();
    const intervalId = window.setInterval(() => void refreshAndReconcile(), PROBE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [capabilitiesQ.refetch, pollingIds.length]);

  const selectedCapabilities = useMemo(
    () => groups
      .flatMap((group) => group.rows.map((row) => row.capability))
      .filter((capability) => selectedIds.has(capability.id)),
    [groups, selectedIds],
  );
  const selectedProbeable = selectedCapabilities.filter(
    (capability) => isProbeable(capability) && !pollingIdSet.has(capability.id),
  );
  const selectedIgnorable = selectedCapabilities.filter((capability) => capability.status !== 'ignored');
  const selectedUnignorable = selectedCapabilities.filter((capability) => capability.status === 'ignored');
  const busy = bulkAction !== null;

  function trackQueuedProbe(capabilityId: string): void {
    pollingStartedAtRef.current.set(capabilityId, Date.now());
    setPollingIds((previous) => (previous.includes(capabilityId) ? previous : [...previous, capabilityId]));
  }

  function stopTracking(capabilityId: string): void {
    setPollingIds((previous) => previous.filter((id) => id !== capabilityId));
    pollingStartedAtRef.current.delete(capabilityId);
  }

  function handleDiscover(channelId: string, channelSlug: string): void {
    // A manual upstream has no remote list to pull — the button opens the
    // paste dialog instead of firing a request that can only 400.
    if (modelDiscovery?.mode === 'manual') {
      setManualDiscoverChannel({ channelId, channelSlug });
      return;
    }
    setDiscoveringChannelId(channelId);
    discover.mutate(
      { channel_id: channelId },
      {
        onSuccess: (rows) =>
          toast.success(t('upstream_detail.routing.toast.models_loaded', { count: rows.length })),
        onError: (err) => toast.error(coerceErrorMessage(err)),
        onSettled: () => setDiscoveringChannelId(null),
      },
    );
  }

  function handleProbe(capability: UpstreamModelCapabilityRecord): void {
    if (capability.probe_protocol === null) {
      toast.error(t('upstream_detail.routing.probe.classify_first'));
      return;
    }
    setProbingCapabilityId(capability.id);
    probeCapability.mutate(capability.id, {
      onSuccess: () => {
        trackQueuedProbe(capability.id);
        toast.success(t('upstream_detail.routing.toast.probe_queued'));
      },
      onError: (err) => toast.error(coerceErrorMessage(err)),
      onSettled: () => setProbingCapabilityId(null),
    });
  }

  function handleSetProtocol(capabilityId: string, value: string): void {
    if (value === UNCLASSIFIED) return;
    const protocol = PROBE_PROTOCOLS.find((candidate) => candidate === value);
    if (protocol === undefined) return;
    setProbeProtocol.mutate(
      { capability_id: capabilityId, probe_protocol: protocol },
      {
        onSuccess: () =>
          toast.success(t('upstream_detail.routing.toast.protocol_set', { count: 1, protocol })),
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  async function handleSaveCost(input: ModelCostSaveInput): Promise<void> {
    setSavingCapabilityId(input.identity.capabilityId);
    try {
      const response = await setModelCosts.mutateAsync({
        costs: [
          {
            upstream_channel_id: input.identity.upstreamChannelId,
            vendor: input.identity.vendor,
            model: input.identity.model,
            cost_multiplier: input.costMultiplier,
            ...(input.windows === undefined ? {} : { windows: input.windows }),
          },
        ],
        operationId: input.operationId,
      });
      // The owner echoes exactly what it wrote; an empty echo means the
      // identity was rejected without an error status, so the operator
      // must not read the optimistic field as saved.
      const confirmed = response.costs.some(
        (cost) =>
          cost.upstream_channel_id === input.identity.upstreamChannelId &&
          cost.model === input.identity.model,
      );
      if (confirmed) {
        toast.success(t('upstream_detail.routing.cost.saved', { model: input.identity.label }));
      } else {
        toast.error(t('upstream_detail.routing.cost.unconfirmed'));
      }
    } catch (err) {
      const message = coerceErrorMessage(err);
      if (input.windows === undefined) {
        toast.error(t('upstream_detail.routing.cost.save_error', { message }));
        return;
      }
      throw err;
    } finally {
      setSavingCapabilityId(null);
    }
  }

  async function openMutationPreview(
    action: 'ignore' | 'unignore',
    capabilityIds: readonly string[],
  ): Promise<void> {
    if (capabilityIds.length === 0) return;
    try {
      const preview = await previewMutation.mutateAsync({ action, capability_ids: capabilityIds });
      setPendingMutation({ action, capabilityIds: [...capabilityIds], preview });
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    }
  }

  async function confirmMutation(): Promise<void> {
    const pending = pendingMutation;
    if (pending === null || (pending.action === 'ignore' && pending.preview.protected)) return;
    setBulkAction(pending.action === 'ignore' ? 'disable' : 'enable');
    try {
      if (pending.capabilityIds.length === 1) {
        const capabilityId = pending.capabilityIds[0]!;
        if (pending.action === 'ignore') {
          await ignoreCapability.mutateAsync(capabilityId);
          stopTracking(capabilityId);
          toast.success(t('upstream_detail.routing.toast.disabled'));
        } else {
          await unignoreCapability.mutateAsync(capabilityId);
          toast.success(t('upstream_detail.routing.toast.enabled'));
        }
      } else if (pending.action === 'ignore') {
        await batchIgnore.mutateAsync(pending.capabilityIds);
        for (const capabilityId of pending.capabilityIds) stopTracking(capabilityId);
        toast.success(t('upstream_detail.routing.toast.bulk_disabled', { count: pending.capabilityIds.length }));
        setSelectedIds(new Set());
      } else {
        await batchUnignore.mutateAsync(pending.capabilityIds);
        toast.success(t('upstream_detail.routing.toast.bulk_enabled', { count: pending.capabilityIds.length }));
        setSelectedIds(new Set());
      }
      setPendingMutation(null);
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    } finally {
      setBulkAction(null);
    }
  }

  async function handleBulkProbe(): Promise<void> {
    if (selectedProbeable.length === 0) return;
    setBulkAction('probe');
    try {
      await batchProbe.mutateAsync(selectedProbeable.map((capability) => capability.id));
      for (const capability of selectedProbeable) trackQueuedProbe(capability.id);
      toast.success(t('upstream_detail.routing.toast.bulk_probe_queued', { count: selectedProbeable.length }));
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    } finally {
      setBulkAction(null);
    }
  }

  async function handleBulkProtocol(protocol: UpstreamProtocol): Promise<void> {
    const targets = selectedCapabilities.filter(
      (capability) =>
        capability.status !== 'missing_from_list' &&
        capability.status !== 'ignored' &&
        capability.probe_protocol !== protocol,
    );
    if (targets.length === 0) return;
    setBulkAction(`protocol_${protocol}`);
    try {
      await batchSetProbeProtocol.mutateAsync({
        capability_ids: targets.map((capability) => capability.id),
        probe_protocol: protocol,
      });
      toast.success(t('upstream_detail.routing.toast.protocol_set', { count: targets.length, protocol }));
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    } finally {
      setBulkAction(null);
    }
  }

  function toggleSelection(capabilityId: string): void {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(capabilityId)) next.delete(capabilityId);
      else next.add(capabilityId);
      return next;
    });
  }

  function toggleGroupSelection(group: ModelSkuGroup): void {
    const groupIds = group.rows.map((row) => row.capability.id);
    const everySelected = groupIds.every((id) => selectedIds.has(id));
    setSelectedIds((previous) => {
      const next = new Set(previous);
      for (const id of groupIds) {
        if (everySelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  const protocolOptions: readonly SelectOption[] = [
    { value: UNCLASSIFIED, label: t('upstream_detail.routing.protocol.unclassified'), disabled: true },
    ...PROBE_PROTOCOLS.map((protocol) => ({ value: protocol, label: protocol })),
  ];

  if (capabilitiesQ.isLoading || channelsQ.isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid="upstream-model-sku-loading">
        {[1, 2, 3].map((index) => <Skeleton key={index} className="h-12 w-full" />)}
      </div>
    );
  }

  return (
    <div id={UPSTREAM_MODEL_SKU_SECTION_ID} className="flex flex-col gap-5" data-testid="upstream-model-sku-table">
      <div>
        <h2 className="m-0 text-section text-paper">{t('upstream_detail.routing.title')}</h2>
        <p className="mb-0 mt-1 text-dense text-paper-low">{t('upstream_detail.routing.subtitle')}</p>
      </div>

      {capabilitiesQ.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3" role="alert">
          <span className="text-dense text-bad-bright">
            {t('upstream_detail.routing.load_error', { message: coerceErrorMessage(capabilitiesQ.error) })}
          </span>
          <Button size="sm" onClick={() => void capabilitiesQ.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}
      {costsQ.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3" role="alert" data-testid="upstream-model-costs-error">
          <span className="text-dense text-bad-bright">
            {t('upstream_detail.routing.cost.load_error', { message: coerceErrorMessage(costsQ.error) })}
          </span>
          <Button size="sm" onClick={() => void costsQ.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}
      {routingSummaryQ.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3" role="alert" data-testid="upstream-model-serving-error">
          <span className="text-dense text-bad-bright">
            {t('upstream_detail.routing.serving.error', { message: coerceErrorMessage(routingSummaryQ.error) })}
          </span>
          <Button size="sm" onClick={() => void routingSummaryQ.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : summaryTruncated ? (
        <p className="m-0 rounded-lg border border-warn-line bg-warn-dim px-4 py-3 text-dense text-warn" role="status" data-testid="upstream-model-sku-summary-truncated">
          {t('upstream_detail.routing.serving.truncated', {
            loaded: routingSummaryQ.data.pagination.returned,
            total: routingSummaryQ.data.pagination.total,
          })}
        </p>
      ) : null}
      {discover.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3" role="alert" data-testid="upstream-model-discover-error">
          <span className="text-dense text-bad-bright">
            {t('upstream_detail.routing.discover.failed', { message: coerceErrorMessage(discover.error) })}
          </span>
        </div>
      ) : null}

      {selectedCapabilities.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-raised px-3 py-2" data-testid="upstream-model-bulk-actions">
          <span className="text-label font-medium text-paper-mid">
            {t('upstream_detail.routing.bulk.selected', { count: selectedCapabilities.length })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {PROBE_PROTOCOLS.map((protocol) => (
              <Button
                key={protocol}
                size="sm"
                onClick={() => void handleBulkProtocol(protocol)}
                disabled={busy || setProbeProtocol.isPending}
                data-testid={`upstream-model-bulk-protocol-${protocol}`}
              >
                {bulkAction === `protocol_${protocol}`
                  ? t('upstream_detail.routing.bulk.protocol_setting')
                  : t('upstream_detail.routing.bulk.protocol', { protocol })}
              </Button>
            ))}
            <Button
              size="sm"
              variant="primary"
              onClick={() => void handleBulkProbe()}
              disabled={busy || selectedProbeable.length === 0}
              data-testid="upstream-model-bulk-probe"
            >
              {bulkAction === 'probe'
                ? t('upstream_detail.routing.bulk.probing')
                : t('upstream_detail.routing.bulk.probe')}
            </Button>
            {selectedUnignorable.length > 0 ? (
              <Button
                size="sm"
                onClick={() => void openMutationPreview('unignore', selectedUnignorable.map((c) => c.id))}
                disabled={busy}
                data-testid="upstream-model-bulk-enable"
              >
                {bulkAction === 'enable'
                  ? t('upstream_detail.routing.bulk.enabling')
                  : t('upstream_detail.routing.bulk.enable')}
              </Button>
            ) : null}
            <Button
              size="sm"
              onClick={() => void openMutationPreview('ignore', selectedIgnorable.map((c) => c.id))}
              disabled={busy || selectedIgnorable.length === 0}
              data-testid="upstream-model-bulk-disable"
            >
              {bulkAction === 'disable'
                ? t('upstream_detail.routing.bulk.disabling')
                : t('upstream_detail.routing.bulk.disable')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} disabled={busy}>
              {t('upstream_detail.routing.bulk.clear')}
            </Button>
          </div>
        </div>
      ) : null}

      {groups.length === 0 ? (
        <p className="m-0 rounded-lg border border-warn-line bg-warn-dim px-4 py-3 text-dense text-warn" role="status" data-testid="upstream-model-sku-no-namespaces">
          {t('upstream_detail.routing.channels.empty')}
        </p>
      ) : (
        groups.map((group) => (
          <section
            key={group.channelId}
            className="overflow-hidden rounded-lg border border-line-soft bg-card"
            data-testid={`upstream-model-sku-group-${group.channelId}`}
          >
            <div className="flex flex-wrap items-center gap-2.5 border-b border-line-soft px-4 py-2.5">
              <h3 className="m-0 font-mono text-section text-paper">{group.channelSlug}</h3>
              <span className="rounded-full bg-signal-dim px-2 py-0.5 font-mono text-micro text-signal">
                {t('upstream_detail.routing.namespace.count', { count: group.rows.length })}
              </span>
              {group.stale ? (
                <Chip tone="warn">{t('upstream_detail.routing.namespace.stale')}</Chip>
              ) : group.enabled ? null : (
                <Chip tone="neutral">{t('upstream_detail.routing.namespace.disabled')}</Chip>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => handleDiscover(group.channelId, group.channelSlug)}
                  loading={discover.isPending && discoveringChannelId === group.channelId}
                  disabled={!group.enabled || discover.isPending}
                  data-testid={`upstream-model-discover-${group.channelId}`}
                >
                  {discover.isPending && discoveringChannelId === group.channelId
                    ? t('upstream_detail.routing.discovering')
                    : t('upstream_detail.routing.discover')}
                </Button>
              </div>
            </div>
            {group.rows.length === 0 ? (
              <p className="m-0 px-4 py-8 text-center text-dense text-paper-low">
                {group.enabled
                  ? t('upstream_detail.routing.namespace.empty')
                  : t('upstream_detail.routing.discover.blocked')}
              </p>
            ) : (
              <Table>
                <THead>
                  <Tr interactive={false}>
                    <Th className="w-[44px]">
                      {/* Scoped to this namespace: a header box that reached
                          across groups would silently select rows the
                          operator cannot see from here. */}
                      <Checkbox
                        checked={group.rows.every((row) => selectedIds.has(row.capability.id))}
                        onCheckedChange={() => toggleGroupSelection(group)}
                        label={<span className="sr-only">{t('upstream_detail.routing.bulk.select_all')}</span>}
                        testId={`upstream-model-select-all-${group.channelId}`}
                      />
                    </Th>
                    <Th>{t('upstream_detail.routing.col.model')}</Th>
                    <Th>{t('upstream_detail.routing.col.protocol')}</Th>
                    <Th>{t('upstream_detail.routing.col.probe')}</Th>
                    <Th>{t('upstream_detail.routing.col.enabled')}</Th>
                    <Th>{t('upstream_detail.routing.col.cost')}</Th>
                    <Th>{t('upstream_detail.routing.col.serving')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {group.rows.map((row) => {
                    const capability = row.capability;
                    const enabled = capability.status !== 'ignored';
                    const polling = pollingIdSet.has(capability.id) && capability.status === 'needs_probe';
                    const identity: ModelCostIdentity = {
                      capabilityId: capability.id,
                      upstreamChannelId: capability.channel_id,
                      vendor: capability.vendor,
                      model: capability.model,
                      label: row.label,
                    };
                    return (
                      <Tr key={capability.id} data-testid={`upstream-model-sku-row-${capability.id}`}>
                        <Td>
                          <Checkbox
                            checked={selectedIds.has(capability.id)}
                            onCheckedChange={() => toggleSelection(capability.id)}
                            label={<span className="sr-only">{t('upstream_detail.routing.bulk.select_row', { model: row.label })}</span>}
                            testId={`upstream-model-select-${capability.id}`}
                          />
                        </Td>
                        <Td>
                          <div className="flex flex-col gap-0.5">
                            <span className="font-mono text-body text-paper">{row.label}</span>
                            <span className="font-mono text-micro text-paper-low">
                              {t('upstream_detail.routing.model.canonical', {
                                vendor: capability.vendor,
                                model: capability.model,
                              })}
                            </span>
                            <span className="font-mono text-micro text-paper-low">
                              {t('upstream_detail.routing.model.key_bucket', { key: capability.key_bucket_id })}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <Select
                            value={capability.probe_protocol ?? UNCLASSIFIED}
                            onValueChange={(value) => handleSetProtocol(capability.id, value)}
                            options={protocolOptions}
                            disabled={setProbeProtocol.isPending || capability.status === 'ignored'}
                            ariaLabel={t('upstream_detail.routing.protocol.label', { model: row.label })}
                            mono
                            testId={`upstream-model-protocol-${capability.id}`}
                          />
                        </Td>
                        <Td>
                          <div className="flex flex-col items-start gap-1">
                            <Chip tone={CAPABILITY_STATUS_TONE[capability.status] ?? 'neutral'}>
                              {capabilityStatusLabel(capability.status, t)}
                            </Chip>
                            {capability.last_passed_at !== null ? (
                              <span className="font-mono text-micro text-paper-low">
                                {t('upstream_detail.routing.probe.last_passed', {
                                  time: formatLocalTimestamp(capability.last_passed_at, { seconds: true }),
                                })}
                              </span>
                            ) : null}
                            {capability.error !== null && capability.error.length > 0 ? (
                              <span className="line-clamp-2 text-micro text-bad">{capability.error}</span>
                            ) : null}
                            {capability.status !== 'missing_from_list' && capability.status !== 'ignored' ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleProbe(capability)}
                                disabled={probeCapability.isPending || polling || capability.probe_protocol === null}
                                className="-ml-2.5 text-signal hover:text-signal-bright"
                                data-testid={`upstream-model-probe-${capability.id}`}
                              >
                                {probingCapabilityId === capability.id
                                  ? t('upstream_detail.routing.probe.running')
                                  : polling
                                    ? t('upstream_detail.routing.probe.waiting')
                                    : capability.probe_protocol === null
                                      ? t('upstream_detail.routing.probe.classify_first')
                                      : capability.status === 'passed'
                                        ? t('upstream_detail.routing.probe.rerun')
                                        : t('upstream_detail.routing.probe.run')}
                              </Button>
                            ) : null}
                          </div>
                        </Td>
                        <Td>
                          <div className="flex flex-col items-start gap-1">
                            <Checkbox
                              checked={enabled}
                              onCheckedChange={() =>
                                void openMutationPreview(enabled ? 'ignore' : 'unignore', [capability.id])
                              }
                              disabled={busy || ignoreCapability.isPending || unignoreCapability.isPending}
                              label={<span className="sr-only">{t('upstream_detail.routing.enable.label', { model: row.label })}</span>}
                              testId={`upstream-model-enable-toggle-${capability.id}`}
                            />
                            <span className="text-micro text-paper-low">
                              {enabled
                                ? t('upstream_detail.routing.enable.on')
                                : t('upstream_detail.routing.enable.off')}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div className="flex flex-col items-start gap-3">
                            <ModelCostCell
                              identity={identity}
                              cost={modelCostFor(costIndex, capability)}
                              costsLoaded={costsQ.isSuccess}
                              saving={setModelCosts.isPending && savingCapabilityId === capability.id}
                              onSave={(input) => { void handleSaveCost(input); }}
                            />
                            <ModelCostWindowsEditor
                              identity={identity}
                              cost={modelCostFor(costIndex, capability)}
                              costsLoaded={costsQ.isSuccess}
                              saving={setModelCosts.isPending && savingCapabilityId === capability.id}
                              onSave={handleSaveCost}
                            />
                          </div>
                        </Td>
                        <Td>
                          <ServingSkuBadge
                            capabilityId={capability.id}
                            count={servedCountFor(capability.id)}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </section>
        ))
      )}

      <CapabilityImpactDialog
        pending={pendingMutation}
        busy={busy}
        onClose={() => setPendingMutation(null)}
        onConfirm={() => void confirmMutation()}
      />

      <ManualDiscoverDialog
        open={manualDiscoverChannel !== null}
        channelId={manualDiscoverChannel?.channelId ?? ''}
        channelSlug={manualDiscoverChannel?.channelSlug ?? ''}
        onClose={() => setManualDiscoverChannel(null)}
        discoverAsync={discover.mutateAsync}
      />
    </div>
  );
}
